import React from 'react';
import { DocumentTextIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';

export default function HRATIAViewer({ hratIA, onExport }) {
  if (!hratIA || !hratIA.rows || hratIA.rows.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
        <DocumentTextIcon className="w-12 h-12 text-green-600 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-green-800 mb-2">No HRAT-IA Required</h3>
        <p className="text-green-700">
          No enrollees with timeliness issues were found. HRAT-IA (Impact Analysis) table is not needed.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center shadow-lg">
              <DocumentTextIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-orange-900 mb-1">
                HRAT-IA (Table 2IA) - Auto-Generated
              </h3>
              <p className="text-orange-700 text-sm">
                Health Risk Assessment Timeliness - Impact Analysis
              </p>
            </div>
          </div>
          
          {onExport && (
            <button
              onClick={() => onExport('hrat-ia')}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <DocumentArrowDownIcon className="w-4 h-4" />
              Export HRAT-IA
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Total Enrollees</p>
          <p className="text-2xl font-bold text-gray-800">{hratIA.count}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Generated Date</p>
          <p className="text-sm font-medium text-gray-800">
            {new Date(hratIA.generatedDate).toLocaleDateString()}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Reason</p>
          <p className="text-sm font-medium text-gray-800">Timeliness Issues</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-max">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">A<br/>First Name</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">B<br/>Last Name</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">C<br/>Enrollee ID</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">D<br/>Contract ID</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">E<br/>PBP</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">F<br/>Plan Type</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">G<br/>Enrollment Date</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">H<br/>IHRA Date</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">I<br/>IHRA Outreach Req</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">J<br/>IHRA Attempted</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">K<br/>First IHRA Date</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">L<br/>Last IHRA Date</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">M<br/>IHRA Refusal</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">N<br/>AHRA Due Date</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">O<br/>AHRA Done?</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">P<br/>AHRA Date</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">Q<br/>AHRA Outreach Req</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">R<br/>AHRA Attempted</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">S<br/>First AHRA Date</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">T<br/>Last AHRA Date</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">U<br/>AHRA Refusal</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">V<br/>UTC Letter Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {hratIA.rows.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-900 text-xs">{row["Enrollee First Name"]}</td>
                  <td className="px-3 py-2 text-gray-900 text-xs">{row["Enrollee Last Name"]}</td>
                  <td className="px-3 py-2 text-gray-600 font-mono text-xs">{row["Enrollee ID"]}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{row["Contract ID"]}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{row["Plan Benefit Package (PBP)"]}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{row["Plan Type"]}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{row["Enrollment effective date"]}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{row["IHRA completion date"]}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs bg-yellow-50">{row["Number of IHRA outreach attempts required by MOC"] || '(To be filled)'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs bg-yellow-50">{row["Number of IHRA outreaches attempted"] || '(To be filled)'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs bg-yellow-50">{row["Date of first IHRA outreach attempt"] || '(To be filled)'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs bg-yellow-50">{row["Date of last IHRA outreach attempt"] || '(To be filled)'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs bg-yellow-50">{row["Date of enrollee IHRA refusal"] || '(To be filled)'}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{row["Annual HRA (AHRA) due date"]}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{row["Was an AHRA completed?"]}</td>
                  <td className="px-3 py-2 text-gray-600 text-xs">{row["AHRA completion date"]}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs bg-yellow-50">{row["Number of AHRA outreach attempts required by MOC"] || '(To be filled)'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs bg-yellow-50">{row["Number of AHRA outreaches attempted"] || '(To be filled)'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs bg-yellow-50">{row["Date of first AHRA outreach attempt"] || '(To be filled)'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs bg-yellow-50">{row["Date of last AHRA outreach attempt"] || '(To be filled)'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs bg-yellow-50">{row["Date of enrollee AHRA refusal"] || '(To be filled)'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs bg-yellow-50">{row["Date the HRA-Enable or Contact (UTC) Letter was sent to non-responding enrollee"] || '(To be filled)'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
        <h4 className="text-lg font-semibold text-amber-800 mb-3">Impact Analysis Instructions</h4>
        <div className="text-amber-700 text-sm space-y-2">
          <p>
            <strong>Purpose:</strong> This table contains enrollees with HRA timeliness issues that require follow-up and impact analysis.
          </p>
          <p>
            <strong>Next Steps:</strong> For each enrollee listed:
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Complete the outreach fields (Method, Date, Result, Follow-up)</li>
            <li>Document any corrective actions taken</li>
            <li>Assess the impact of the untimely HRA on care coordination</li>
            <li>Update the Notes field with relevant findings</li>
          </ul>
          <p>
            <strong>Export:</strong> Use the "Export HRAT-IA" button to download this table as an Excel file for completion and submission.
          </p>
        </div>
      </div>
    </div>
  );
}
